import { API_PATHS, API_V1_PREFIX, type Job } from '@uroute/shared';
import { Button, FormField, useToast } from '@uroute/ui';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { haptic } from '../lib/haptics';
import { useOnline } from '../lib/offline';
import { makeItem, newId, submitItems, type QueueItem } from '../lib/queue';
import { invalidate } from '../lib/useQuery';
import { Sheet } from './Sheet';
import { Camera, ImageIcon, Trash, WifiOff } from './icons';

const EXCEPTION_TYPES = [
  { value: 'delivery_failed', label: 'Delivery failed' },
  { value: 'recipient_absent', label: 'Recipient absent' },
  { value: 'damaged', label: 'Damaged goods' },
  { value: 'refused', label: 'Refused by recipient' },
  { value: 'access_issue', label: 'Access issue' },
  { value: 'other', label: 'Other' },
] as const;

interface Evidence {
  id: string;
  file: File;
  url: string;
}

export interface ExceptionSheetProps {
  open: boolean;
  onClose: () => void;
  job: Job;
}

/** Report a delivery exception ??? notes + optional evidence photo, notifies dispatch. */
export function ExceptionSheet({ open, onClose, job }: ExceptionSheetProps) {
  const toast = useToast();
  const online = useOnline();
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  const [type, setType] = useState<string>('delivery_failed');
  const [notes, setNotes] = useState('');
  const [evidence, setEvidence] = useState<Evidence | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) return;
    setEvidence((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });
    setNotes('');
    setType('delivery_failed');
    setError(null);
    setSubmitting(false);
  }, [open]);

  const pick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      setError('Evidence photos must be smaller than 15 MB.');
      return;
    }
    setEvidence((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return { id: newId(), file, url: URL.createObjectURL(file) };
    });
  };

  const submit = async () => {
    setError(null);
    const text = notes.trim();
    if (text.length < 3) {
      setError('Describe what happened (at least a few words).');
      haptic('error');
      return;
    }
    setSubmitting(true);
    try {
      const items: QueueItem[] = [];
      let previous: string | undefined;

      if (evidence) {
        const proofItem = makeItem({
          kind: 'proof',
          label: `Evidence for ${job.externalRef}`,
          path: API_PATHS.proofs,
          jobId: job.id,
          fields: { jobId: job.id, type: 'photo', notes: `Exception evidence: ${text.slice(0, 200)}` },
          files: [{ field: 'file', name: evidence.file.name, type: evidence.file.type, blob: evidence.file }],
        });
        items.push(proofItem);
        previous = proofItem.id;
      }

      items.push(
        makeItem({
          kind: 'exception',
          label: `Exception for ${job.externalRef}`,
          path: `${API_V1_PREFIX}/exceptions`,
          jobId: job.id,
          json: { jobId: job.id, type, notes: text },
          dependsOn: previous,
        }),
      );

      const result = await submitItems(items);
      if (result.status === 'done') {
        toast.show({
          title: 'Exception reported',
          description: 'Dispatch has been notified and the job is flagged.',
          tone: 'warning',
        });
        haptic('error');
        invalidate('me/jobs');
        invalidate(`job/${job.id}`);
        onClose();
        return;
      }
      if (result.status === 'queued') {
        toast.show({
          title: 'Report saved on this device',
          description: 'It will reach dispatch when you are back online.',
          tone: 'warning',
          duration: 6000,
        });
        invalidate('me/jobs');
        onClose();
        return;
      }
      setError(result.error);
      haptic('error');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Please try again.');
      haptic('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title="Report an exception"
      testId="exception-sheet"
      disableBackdropClose={submitting}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void submit()}
            loading={submitting}
            data-testid="exception-submit"
            className="sheet__primary"
          >
            Send report
          </Button>
        </>
      }
    >
      {!online ? (
        <p className="notice notice--warning" data-testid="exception-offline">
          <WifiOff size={16} />
          <span>You&rsquo;re offline — the report is saved and sends when you reconnect.</span>
        </p>
      ) : null}

      <div className="stack">
        <FormField label="What happened?" required>
          {({ id, describedBy }) => (
            <select
              id={id}
              aria-describedby={describedBy}
              className="u-select"
              value={type}
              onChange={(e) => setType(e.target.value)}
              data-testid="exception-type"
            >
              {EXCEPTION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          )}
        </FormField>

        <FormField label="Notes" required hint="Include what you tried and where you left the parcel">
          {({ id, describedBy }) => (
            <textarea
              id={id}
              aria-describedby={describedBy}
              className="u-input u-textarea"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Nobody answering at goods-in, called twice, parcel left with neighbour at no. 14"
              data-testid="exception-notes"
            />
          )}
        </FormField>

        <div>
          <span className="u-field__label">Evidence photo</span>
          <div className="pod__buttons">
            <Button variant="secondary" onClick={() => cameraRef.current?.click()} data-testid="exception-take-photo">
              <Camera size={16} />
              Take photo
            </Button>
            <Button variant="ghost" onClick={() => galleryRef.current?.click()} data-testid="exception-add-photo">
              <ImageIcon size={16} />
              Choose
            </Button>
          </div>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            aria-label="Take an evidence photo with the camera"
            data-testid="exception-photo-input"
            onChange={pick}
          />
          <input
            ref={galleryRef}
            type="file"
            accept="image/*"
            className="sr-only"
            aria-label="Choose an evidence photo from the gallery"
            data-testid="exception-gallery-input"
            onChange={pick}
          />
          {evidence ? (
            <div className="pod__thumb pod__thumb--single">
              <img src={evidence.url} alt="Evidence preview" />
              <button
                type="button"
                className="pod__thumbremove"
                onClick={() => {
                  setEvidence((prev) => {
                    if (prev) URL.revokeObjectURL(prev.url);
                    return null;
                  });
                }}
                aria-label="Remove evidence photo"
                data-testid="exception-remove-photo"
              >
                <Trash size={14} />
              </button>
            </div>
          ) : (
            <p className="hint">Optional, but it helps dispatch resolve it faster.</p>
          )}
        </div>

        {error ? (
          <p className="form-error" role="alert" data-testid="exception-error">
            {error}
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}
